import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Headphones } from "lucide-react";
import { FaInstagram, FaSpotify, FaYoutube, FaApple } from "react-icons/fa";
import WolfMark from "@/components/WolfMark";
import { WolfDawgSection } from "@/components/WolfDawgSection";

const HERO_BG_DESKTOP = "/wolf-hero-desktop.png";
const HERO_BG_MOBILE = "/wolf-hero-mobile.png";

const OCEAN_1 = "https://images.unsplash.com/photo-1759759261832-867ced37d3e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxkYXJrJTIwb2NlYW4lMjB3YXZlcyUyMGNsaWZmcyUyMG1vb2R5fGVufDB8fHx8MTc3OTMyMTA1N3ww&ixlib=rb-4.1.0&q=85";
const OCEAN_2 = "https://images.unsplash.com/photo-1769269892590-18bed18fc96c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxkYXJrJTIwb2NlYW4lMjB3YXZlcyUyMGNsaWZmcyUyMG1vb2R5fGVufDB8fHx8MTc3OTMyMTA1N3ww&ixlib=rb-4.1.0&q=85";
const STUDIO = "https://images.pexels.com/photos/15129779/pexels-photo-15129779.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const NOTHING_MISSING_COVER = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/iurenmkw_2A418EAD-D8A5-4F55-88D1-E32B846F8093.png";

const REBORN_COVER = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/ixucow66_D15A2D70-A49B-4B34-B2A1-0D7BFAD5A4BA.png";

const WOLF_ALBUM_COVER = "https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/pbp5yabt_IMG_2649.jpeg";

const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  show: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.2, 0.7, 0.2, 1] },
  }),
};

export default function Home() {
  return (
    <div className="bg-[#080808]" data-testid="home-page">
      {/* HERO — Luxury Black & Gold (matches reference) */}
      <section className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-black" data-testid="hero-section">
        {/* Mobile portrait wolf */}
        <div
          className="absolute inset-0 hero-drift sm:hidden"
          style={{
            backgroundImage: `url(${HERO_BG_MOBILE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Desktop landscape wolf */}
        <div
          className="absolute inset-0 hero-drift hidden sm:block"
          style={{
            backgroundImage: `url(${HERO_BG_DESKTOP})`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        />
        {/* Subtle darken on left so gold text reads — softer on mobile (image has built-in left space) */}
        <div
          className="absolute inset-0 pointer-events-none hidden sm:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.25) 65%, rgba(0,0,0,0.10) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none sm:hidden"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.10) 30%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.95) 100%)",
          }}
        />
        {/* Bottom fade to black for footer row */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />
        <div className="smoke-layer" />
        <div className="dust-particles" />

        <div className="relative h-full max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-end pb-28 sm:pb-32">
          <motion.h1
            initial="hidden" animate="show" variants={fadeUp} custom={1}
            data-testid="hero-title"
            className="font-display wolf-gold-foil leading-[0.82] text-[26vw] sm:text-[18vw] lg:text-[13rem] tracking-tighter"
          >
            WOLF
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" variants={fadeUp} custom={2}
            className="font-editorial italic text-[#D4AF37]/90 text-2xl sm:text-3xl mt-4 max-w-2xl"
            data-testid="hero-tagline"
          >
            Forged in the dark. Still standing.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp} custom={3}
            className="mt-10 sm:mt-12 flex flex-col gap-3 sm:gap-4 w-full max-w-md sm:max-w-xl"
          >
            <Link
              to="/music"
              data-testid="hero-listen-btn"
              className="group relative w-full flex items-center justify-between gap-3 btn-gold-fill text-black font-display tracking-[0.45em] text-sm sm:text-base px-6 py-5 border border-[#D4AF37] transition-all"
            >
              <Headphones size={18} strokeWidth={2.2} />
              <span className="flex-1 text-center">LISTEN NOW</span>
              <ArrowRight size={18} strokeWidth={2.2} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/den"
              data-testid="hero-den-btn"
              className="group w-full flex items-center justify-between gap-3 border border-[#D4AF37] text-[#D4AF37] font-display tracking-[0.45em] text-sm sm:text-base px-6 py-5 hover:bg-[#D4AF37]/10 transition-all"
            >
              <span className="w-[18px]" />
              <span className="flex-1 text-center">ENTER THE DEN</span>
              <ArrowRight size={18} strokeWidth={2.2} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/media"
              data-testid="hero-visuals-btn"
              className="group w-full inline-flex items-center gap-3 text-[#D4AF37]/80 font-display tracking-[0.45em] text-sm sm:text-base px-1 py-3 hover:text-[#D4AF37] transition-all"
            >
              <Play size={18} strokeWidth={2.2} />
              <span>WATCH VISUALS</span>
            </Link>
          </motion.div>
        </div>

        {/* Bottom social icon row — gold */}
        <div
          data-testid="hero-social-row"
          className="absolute bottom-6 sm:bottom-8 left-0 right-0 px-6 sm:px-10 lg:px-16"
        >
          <div className="max-w-[1500px] mx-auto flex items-center justify-between border-t border-[#D4AF37]/20 pt-5">
            <div className="flex items-center gap-7 sm:gap-10 text-[#D4AF37]">
              <a href="https://instagram.com/wolfmusicau" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="hero-social-instagram" className="hover:text-[#F4C542] transition-colors">
                <FaInstagram size={22} />
              </a>
              <a href="https://open.spotify.com/artist/3b0xMgt8u93GykFDiAXslc?si=zanefZxySXKyZg1kTiTFdQ" target="_blank" rel="noreferrer" aria-label="Spotify" data-testid="hero-social-spotify" className="hover:text-[#F4C542] transition-colors">
                <FaSpotify size={22} />
              </a>
              <a href="https://youtube.com/@WOLFMUSICAU" target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="hero-social-youtube" className="hover:text-[#F4C542] transition-colors">
                <FaYoutube size={24} />
              </a>
              <a href="https://music.apple.com/au/artist/wolf/1470858095" target="_blank" rel="noreferrer" aria-label="Apple Music" data-testid="hero-social-apple" className="hover:text-[#F4C542] transition-colors">
                <FaApple size={22} />
              </a>
            </div>
            <div className="text-[#D4AF37] opacity-80">
              <WolfMark className="w-8 h-8" />
            </div>
          </div>
        </div>
      </section>

      {/* GODWOLF TEASER STRIP */}
      <Link
        to="/media"
        data-testid="godwolf-teaser-strip"
        className="group block border-y border-[#D4AF37]/30 bg-[#0d0d0d] hover:bg-[#111] transition-colors"
      >
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 py-6 sm:py-8 flex items-center gap-5 sm:gap-8">
          <img
            src="/godwolf-cover.jpg"
            alt="GODWOLF — the new album"
            className="w-16 h-16 sm:w-20 sm:h-20 object-cover border border-[#D4AF37]/30 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/80">— New Album</p>
            <p className="font-display text-2xl sm:text-4xl leading-tight mt-1">
              <span className="wolf-gold-foil">GODWOLF</span>
              <span className="text-white/40 mx-3">·</span>
              <span className="text-white">Out Now</span>
            </p>
          </div>
          <span className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] border border-[#D4AF37]/40 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 px-5 py-3 whitespace-nowrap transition-all">
            Watch the "Glory Be" trailer
            <span className="group-hover:translate-x-1 transition-transform" aria-hidden>→</span>
          </span>
          <span className="sm:hidden text-[#D4AF37] text-xl" aria-hidden>→</span>
        </div>
      </Link>

      {/* PEACE WITH TEETH TEASER STRIP */}
      <a
        href="https://www.bandlab.com/wolf__reborn/albums/36b64586-b8b4-f111-a6a9-000d3a96ed93"
        target="_blank"
        rel="noreferrer"
        data-testid="pwt-teaser-strip"
        className="group block border-b border-[#D4AF37]/30 bg-[#0a0a0a] hover:bg-[#101010] transition-colors"
      >
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 py-6 sm:py-8 flex items-center gap-5 sm:gap-8">
          <img
            src="/peace-with-teeth-cover.jpg"
            alt="Peace With Teeth — the BandLab album"
            className="w-16 h-16 sm:w-20 sm:h-20 object-cover border border-[#D4AF37]/30 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/80">— 39-Track Album</p>
            <p className="font-display text-2xl sm:text-4xl leading-tight mt-1">
              <span className="wolf-gold-foil">PEACE WITH TEETH</span>
              <span className="text-white/40 mx-3">·</span>
              <span className="text-white">Stream Free</span>
            </p>
          </div>
          <span className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] border border-[#D4AF37]/40 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 px-5 py-3 whitespace-nowrap transition-all">
            Listen free on BandLab
            <span className="group-hover:translate-x-1 transition-transform" aria-hidden>→</span>
          </span>
          <span className="sm:hidden text-[#D4AF37] text-xl" aria-hidden>→</span>
        </div>
      </a>

      {/* MARQUEE BAND */}
      <section aria-hidden className="overflow-hidden border-y border-white/10 py-6 bg-black">
        <div className="marquee-track whitespace-nowrap flex gap-16 font-display tracking-extreme text-2xl sm:text-3xl text-white/30">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-16 shrink-0">
              <span>Reborn</span><span className="text-[#D4AF37]">✦</span>
              <span>Wolf</span><span className="text-[#D4AF37]">✦</span>
              <span>Byron Air</span><span className="text-[#D4AF37]">✦</span>
              <span>Yukon Wheels</span><span className="text-[#D4AF37]">✦</span>
              <span>For You</span><span className="text-[#D4AF37]">✦</span>
              <span>Nothing Missing</span><span className="text-[#D4AF37]">✦</span>
              <span>Legend</span><span className="text-[#D4AF37]">✦</span>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO / MANIFESTO */}
      <section className="py-28 sm:py-40 px-6 sm:px-10 lg:px-16 max-w-[1500px] mx-auto" data-testid="manifesto-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-6">— The Project</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.9]">
              A world<br/>
              <span className="text-[#D4AF37]/90">built in the dark</span><br/>
              and shaped<br/>through rebirth.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-8">
            <p className="font-editorial italic text-2xl sm:text-3xl text-white/80 leading-snug">
              "Some nights the only thing I had was the sound of the ocean and a half-finished song.
              That was enough. That was always enough."
            </p>
            <p className="font-body text-white/55 mt-8 text-base sm:text-lg leading-relaxed max-w-xl">
              WOLF is the music, writing, and lifestyle project of Mark Leslie. Heavy atmospheric rock
              meets ocean energy. A pack for the ones who keep walking through the long dark and never
              ask the night for permission.
            </p>
            <Link
              to="/about"
              data-testid="manifesto-about-link"
              className="inline-flex items-center gap-3 mt-10 text-[11px] uppercase tracking-[0.32em] text-white border-b border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] pb-2 transition-all"
            >
              The Story <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* THE ARTIST */}
      <section className="py-28 sm:py-40 px-6 sm:px-10 lg:px-16 max-w-[1500px] mx-auto" data-testid="artist-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:col-span-7 relative aspect-square overflow-hidden"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_wolf-cinematic/artifacts/qatqt3if_ChatGPT%20Image%20Jun%201%2C%202026%2C%2005_24_36%20PM.png"
              alt="Mark Leslie — WOLF"
              className="w-full h-full object-cover"
              data-testid="artist-portrait"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— The Artist</p>
            <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl text-white leading-[0.85]">
              Mark<br/>Leslie.
            </h2>
            <p className="font-editorial italic text-xl sm:text-2xl text-white/75 mt-8 leading-snug">
              "The sound of survival<br className="hidden sm:inline"/> turning into purpose."
            </p>
            <p className="font-body text-white/55 mt-6 leading-relaxed max-w-md">
              Australian songwriter. WOLF is his cinematic music and lifestyle project — heavy
              atmospheric rock, raw honesty, faith, surf, and rebirth.
            </p>
            <Link
              to="/about"
              data-testid="artist-about-link"
              className="inline-flex items-center gap-3 mt-10 text-[11px] uppercase tracking-[0.32em] text-white border-b border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] pb-2 transition-all"
            >
              Read the full story <ArrowRight size={14}/>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED RELEASES */}
      <section
        className="relative py-28 sm:py-36 px-6 sm:px-10 lg:px-16 max-w-[1500px] mx-auto"
        data-testid="featured-releases-section"
      >
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— Featured releases</p>
            <h2 className="font-display text-5xl sm:text-7xl text-white leading-[0.9]">The Catalog</h2>
          </div>
          <Link
            to="/music"
            data-testid="releases-all-link"
            className="text-[11px] uppercase tracking-[0.32em] text-white/70 hover:text-[#D4AF37] inline-flex items-center gap-2 border-b border-white/20 pb-2"
          >
            All music <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { t: "Reborn", y: "Coming Jun 5, 2026", type: "Album", k: "A statement. A resurrection.", img: REBORN_COVER },
            { t: "Nothing Missing", y: "Apr 2026", type: "Album", k: "A goodbye and a rebirth.", img: NOTHING_MISSING_COVER },
            { t: "Wolf", y: "Mar 2026", type: "Album", k: "The debut. Self-titled.", img: WOLF_ALBUM_COVER },
          ].map((r, i) => (
            <motion.div
              key={r.t}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="group relative lift bg-[#161616] border border-white/10 overflow-hidden"
              data-testid={`featured-release-${i}`}
            >
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={r.img}
                  alt={r.t}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/90">{r.y} · {r.type}</p>
                  <h3 className="font-display text-4xl text-white mt-2">{r.t}</h3>
                  <p className="font-editorial italic text-white/55 mt-1">{r.k}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WOLF DAWG LAUNCH ANNOUNCEMENT */}
      <WolfDawgSection />

      {/* SCRIPTURE — center of the experience */}
      <section className="relative py-32 sm:py-48 px-6 sm:px-10 lg:px-16 overflow-hidden" data-testid="scripture-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-[700px] max-h-[700px] rounded-full bg-[#7A5A18]/15 blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.1 }}
          className="relative max-w-4xl mx-auto text-center"
          data-testid="scripture-quote"
        >
          <p className="text-[11px] uppercase tracking-[0.45em] text-[#D4AF37]/90 mb-10">— Scripture</p>
          <p className="font-editorial italic text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.15] wolf-glow">
            "Whatever you do,<br className="hidden sm:inline" /> do it all for the glory of God."
          </p>
          <p className="font-display tracking-extreme text-[#D4AF37]/90 text-sm sm:text-base mt-12">
            — 1 Corinthians 10:31
          </p>
        </motion.div>
      </section>

      {/* IMAGE STRIPE */}
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden" data-testid="image-stripe">
        <div
          className="absolute inset-0 hero-drift"
          style={{ backgroundImage: `url(${OCEAN_1})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center">
          <div className="max-w-xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— Roll Call</p>
            <h3 className="font-display text-5xl sm:text-7xl text-white leading-[0.9]">
              No pep talks.<br/>Just proof of life.
            </h3>
            <p className="font-editorial italic text-white/70 text-lg sm:text-xl mt-6 max-w-md">
              The Den is a private feed for the ones who keep showing up. Three reactions. No comments. No noise.
            </p>
            <Link
              to="/den"
              data-testid="stripe-den-btn"
              className="inline-flex items-center gap-3 mt-8 border border-white/30 text-white font-display tracking-extreme text-sm px-7 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              Enter The Den <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FIRE PAGES PREVIEW */}
      <section className="py-28 sm:py-36 px-6 sm:px-10 lg:px-16 max-w-[1500px] mx-auto" data-testid="fire-preview">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-4">— Fire Pages</p>
            <h2 className="font-display text-5xl sm:text-7xl text-white leading-[0.9]">
              Words<br/>from the dark.
            </h2>
            <p className="font-editorial italic text-white/55 mt-6 text-lg max-w-md">
              Poetry, memoir, music meanings. The atmosphere behind the records.
            </p>
            <Link
              to="/fire"
              data-testid="fire-preview-link"
              className="inline-flex items-center gap-3 mt-8 text-[11px] uppercase tracking-[0.32em] text-white border-b border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] pb-2"
            >
              Open the Fire Pages <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {[
              { t: "Built In The Dark", k: "Reflection", e: "Some men are forged in firelight. Others in the cold black hours nobody talks about." },
              { t: "The Wolf Within", k: "Memoir", e: "The wind here doesn't lie. It strips you to what's real and then it lets you breathe." },
              { t: "The Wolf And The Water", k: "Poetry", e: "He learned silence from the ocean. He learned loyalty from the pack." },
            ].map((p, i) => (
              <div
                key={p.t}
                className="border-b border-white/10 py-6 group hover:pl-3 transition-all duration-500"
                data-testid={`fire-preview-${i}`}
              >
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/80">{p.k}</p>
                <h4 className="font-display text-3xl sm:text-4xl text-white mt-2 group-hover:text-[#D4AF37] transition-colors">{p.t}</h4>
                <p className="font-editorial italic text-white/55 mt-2 max-w-2xl">{p.e}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING IMAGE */}
      <section className="relative h-[80vh] min-h-[520px] overflow-hidden" data-testid="closing-image">
        <div
          className="absolute inset-0 hero-drift"
          style={{ backgroundImage: `url(${OCEAN_2})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black" />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 flex flex-col items-start justify-center">
          <p className="font-editorial italic text-white/80 text-2xl sm:text-3xl max-w-2xl leading-snug">
            "Strength isn't loud.<br/>Strength is the man who showed up again."
          </p>
          <p className="mt-6 text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]/90">— The Code</p>
        </div>
      </section>
    </div>
  );
}
